import type { SessionResult } from '../api/sessions'

/**
 * The handoff into Studio as a child.
 *
 * Studio keeps its own session — an `auth` blob in local storage and a cookie
 * of the same name — and never reads the cookies this bundle sets, so it has
 * to be told. Its handoff route receives the child's own session, minted by
 * the platform at the parent's request: the token, the user id and the
 * username the child signs in with, plus a `next` path. It stores them the
 * way its login form does and continues.
 *
 * Everything travels in the fragment. Browsers never send a fragment to the
 * server or in a referrer, so the token stays out of logs, and the route
 * drops it on arrival.
 */
export const STUDIO_HANDOFF_PATH = '/login/handoff'

/** What Studio's handoff route is given: the child's session, and where to continue. */
export interface StudioHandoff {
  accessToken: string
  userId: string
  /** What the child types to sign in, which Studio keeps beside the token. */
  username: string
  /** Where Studio continues to once signed in. */
  next?: string
}

/**
 * The handoff a child's session yields. The platform includes the child on
 * the session; a reply without them has no username to hand over, which is
 * a bug to surface rather than a partial handoff to make.
 */
export function toStudioHandoff({ accessToken, userId, user }: SessionResult): StudioHandoff {
  if (!user) throw new Error('Session did not include the user')
  return { accessToken, userId, username: user.username }
}

export function studioHandoffUrl(
  studioUrl: string,
  { accessToken, userId, username, next = '/' }: StudioHandoff,
): string {
  const fragment = new URLSearchParams({
    access_token: accessToken,
    current_user_id: userId,
    username,
    next,
  })
  return `${studioUrl.replace(/\/+$/, '')}${STUDIO_HANDOFF_PATH}#${fragment}`
}
